import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import Dotdotdot from 'react-dotdotdot'
import insert from 'ramda/es/insert'

import { SwipeCardActionButton, MatchAvatar } from 'components'
import { logEvent } from 'services/helpers'
import { ENTER_KEY_CODE, LEFT_ARROW_KEY_CODE, RIGHT_ARROW_KEY_CODE } from 'constants/common'

import SkipIcon from 'static/images/minusSkip.svg'
import LikeIcon from 'static/images/heartLike.svg'

import c from './SavedCard.module.scss'


const selectCardAnim = (type) => type === 'skip' ? c.leftCardAnim : c.rightCardAnim

class SavedCard extends PureComponent {
  state = {
    animOut: false,
    x: 0,
    y: 0,
    startPosition: { x: 0, y: 0 },
    pristine: true
  }

  isMouseDown = false
  mouseStartX = 0
  mouseStartY = 0
  isMoving = false
  justFinishedDragging = false

  onActionClick = () => {
    this.setState({ animOut: Math.random() + 1 })

    setTimeout(() => {
      const { onActionClick, logType } = this.props
      onActionClick()

      if (logType.includes('Skipped')) {
        logEvent('action.Saved.Like')
      } else if (logType.includes('Liked.OneWay')) {
        logEvent('action.Saved.OneWay.Skip')
      } else if (logType.includes('Liked.OneWay')) {
        logEvent('action.Saved.Mutual.Skip')
      }
    }, 100)
  }

  onRequestClick = () => {
    this.props.onRequestClick()
    logEvent(`action.${this.props.logType}.Request`)
  }

  onStartChatClick = () => {
    const { logType, onStartChatClick } = this.props
    onStartChatClick()
    logEvent(`action.${logType}.Chat`)
  }

  onKeyDown = (event) => {
    switch (event.keyCode) {
      case LEFT_ARROW_KEY_CODE:
        if (this.props.onActionClick && this.props.action === "skip") {
          this.props.onActionClick();
        }
        break;
      case RIGHT_ARROW_KEY_CODE:
        if (this.props.onActionClick && this.props.action === "like") {
          this.props.onActionClick();
        }
        break;
      case ENTER_KEY_CODE:
        if (event.target.tagName === "ARTICLE") {
          this.props.onItemClick()
        }
        break;
      default:
        break
    }
  }

  resetPosition = () => {
    this.setState({
      x: 0,
      y: 0,
      startPosition: { x: 0, y: 0 }
    })
  }

  getDirection = () => {
    const { x } = this.state
    const threshold = 50

    if (x < -threshold) return 'Left'
    if (x > threshold) return 'Right'
    return false
  }

  calculatePosition(deltaX, deltaY) {
    const { startPosition: { x, y } } = this.state
    return {
      x: x + deltaX,
      y: y + deltaY
    }
  }

  onMouseDown = (event) => {
    // Only handle left mouse button
    if (event.button !== 0) return

    // Don't start drag on buttons - let them handle their own clicks
    if (event.target.closest('button')) return

    this.isMouseDown = true
    this.mouseStartX = event.clientX
    this.mouseStartY = event.clientY

    const { x, y } = this.state
    this.setState({
      startPosition: { x, y },
      pristine: false
    })
  }

  onMouseMove = (event) => {
    if (!this.isMouseDown) return

    const deltaX = event.clientX - this.mouseStartX
    const deltaY = event.clientY - this.mouseStartY

    // Only start moving if dragged more than 5px to avoid accidental drags
    if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
      event.preventDefault()
      this.isMoving = true

      this.setState({
        ...this.calculatePosition(deltaX, deltaY)
      })
    }
  }

  onMouseUp = (event) => {
    if (!this.isMouseDown) return

    this.isMouseDown = false
    const direction = this.getDirection()
    const { action } = this.props

    // Set flag if we were dragging
    if (this.isMoving) {
      this.justFinishedDragging = true
      // Reset the flag after a short delay (after click event would have fired)
      setTimeout(() => { this.justFinishedDragging = false }, 100)
    }

    setTimeout(() => { this.isMoving = false }, 0)

    // Check if we should trigger an action based on direction and current action
    let shouldTriggerAction = false

    if (direction === 'Right' && action === 'like') {
      // Swiped right on skipped card - like it
      shouldTriggerAction = true
    } else if (direction === 'Left' && action !== 'like') {
      // Swiped left on liked card - skip it
      shouldTriggerAction = true
    }

    if (shouldTriggerAction) {
      this.onActionClick()
    } else {
      // Reset position if not swiped far enough or wrong direction
      this.resetPosition()
    }
  }

  onMouseLeave = (event) => {
    if (!this.isMouseDown) return
    this.onMouseUp(event)
  }

  onClickCapture = (event) => {
    // Prevent any clicks if we were dragging or just finished dragging
    if (this.isMoving || this.justFinishedDragging) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  componentDidMount() {
    if (this.cardRef) {
      // Use capture phase to catch mousedown before child elements can stopPropagation
      this.cardRef.addEventListener('mousedown', this.onMouseDown, true)
      // Use capture phase to prevent clicks when dragging
      this.cardRef.addEventListener('click', this.onClickCapture, true)
      document.addEventListener('mousemove', this.onMouseMove)
      document.addEventListener('mouseup', this.onMouseUp)
      this.cardRef.addEventListener('mouseleave', this.onMouseLeave)
    }
  }

  componentWillUnmount() {
    if (this.cardRef) {
      this.cardRef.removeEventListener('mousedown', this.onMouseDown, true)
      this.cardRef.removeEventListener('click', this.onClickCapture, true)
      document.removeEventListener('mousemove', this.onMouseMove)
      document.removeEventListener('mouseup', this.onMouseUp)
      this.cardRef.removeEventListener('mouseleave', this.onMouseLeave)
    }
  }

  setCardRef = (ref) => {
    this.cardRef = ref
  }

  renderLikeOverlay = () => {
    const { x } = this.state
    const { action } = this.props

    // Only show like overlay when swiping right on a skipped card
    if (action !== 'like') return null
    if (x <= 0) return null

    const opacityLike = x

    const likeStyle = {
      opacity: opacityLike / 100
    }

    return (
      <div className={c.likeOverlay} style={likeStyle}>
        <img src={LikeIcon} alt='Like' aria-hidden='true' />
        <span>Like</span>
      </div>
    )
  }

  renderSkipOverlay = () => {
    const { x } = this.state
    const { action } = this.props

    // Only show skip overlay when swiping left on a liked card
    if (action === 'like') return null
    if (x >= 0) return null

    const opacitySkip = -x

    const skipStyle = {
      opacity: opacitySkip / 100
    }

    return (
      <div className={c.skipOverlay} style={skipStyle}>
        <img src={SkipIcon} alt='Skip' aria-hidden='true' />
        <span>Skip</span>
      </div>
    )
  }

  onClick = () => {
    if (!this.isMoving) {
      this.props.onItemClick()
    }
  }

  render() {
    const { animOut, x, y } = this.state
    const {
      action, onItemClick, logType,
      onLikeClick,
      onSkipOneWayClick, onSkipMutualClick,
      data: { avatar, match, fullName, claimed }
    } = this.props
    const username = fullName.length > 15
      ? insert(14, ' ', fullName).join('')
      : fullName
    const isGreen = match >= 50
    const isClaimed = claimed !== false

    // Calculate transform and opacity like DraggableCard
    const translate = `translate3d(${x}px, ${y}px, 0px)`
    const opacity = ((250 - Math.abs(x)) / 100)
    const rotate = `rotate(${x / 33}deg)`
    const perspective = 'perspective(38px)'

    const cardStyle = {
      transform: [perspective, translate, rotate].join(' '),
      WebkitTransform: [perspective, translate, rotate].join(' '),
      msTransform: [perspective, translate, rotate].join(' '),
      opacity,
      transition: x === 0 && y === 0 ? 'all 0.3s ease-out' : 'none',
      cursor: this.isMoving ? 'grabbing' : 'pointer'
    }

    return (
      <li className={cn(c.wrapper, animOut && c.animOut)}>
        <article
          ref={this.setCardRef}
          tabIndex={0}
          onClick={this.onClick}
          onKeyDown={this.onKeyDown}
          className={cn(c.container, animOut && selectCardAnim(action))}
          style={cardStyle}
        >
          {this.renderLikeOverlay()}
          {this.renderSkipOverlay()}

          <h2 className={c.name}>
            <Dotdotdot clamp={1}>{username}</Dotdotdot>
          </h2>

          <div className={c.avatarWrapper}>
            <MatchAvatar data={{ avatar, match, claimed: isClaimed }} red={!isGreen} />
          </div>
          <div className={c.buttonsWrapper}>
            {action !== 'like' && <SwipeCardActionButton type="skip" onClick={logType === 'Liked.OneWay' ? onSkipOneWayClick : onSkipMutualClick} />}
            <SwipeCardActionButton
              type="chat"
              onClick={this.onStartChatClick}
              disabled={!isClaimed}
              tooltip={!isClaimed ? "You'll be able to start a conversation once this user registers their account." : ''}
            />
            <SwipeCardActionButton type="view" onClick={onItemClick} />
            <SwipeCardActionButton type="request" onClick={this.onRequestClick} />
            {action === 'like' && <SwipeCardActionButton type="like" onClick={onLikeClick} /> }

          </div>

        </article>
      </li>
    )
  }
}

SavedCard.propTypes = {
  action: PropTypes.string.isRequired,
  onActionClick: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  data: PropTypes.shape({
    avatar: PropTypes.string,
    match: PropTypes.number,
    fullName: PropTypes.string,
    description: PropTypes.string,
    common: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired
}

export { SavedCard }
